import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Video, Award, Clock } from "lucide-react";

const ymd = (d) => d.toISOString().slice(0, 10);
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

export default function ProfessionalDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [pro, setPro] = useState(null);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [reason, setReason] = useState("");
  const [booking, setBooking] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => { api.get(`/professionals/${id}`).then((r) => setPro(r.data)); }, [id]);
  useEffect(() => {
    if (!open || !date) return;
    setLoadingSlots(true);
    setSlot("");
    api.get(`/availability/${id}/slots?date=${ymd(date)}`)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [open, date, id]);

  const book = async () => {
    if (!user) { nav("/login"); return; }
    // Gate: pré-anamnese obrigatória para pacientes particulares (SUS pode ser preenchida depois)
    if (user.role === "patient" && !user.pre_anamnesis_completed) {
      toast.info("Preencha a pré-consulta antes de agendar.");
      nav("/pre-anamnese");
      return;
    }
    if (!slot) { toast.error("Selecione um horário"); return; }
    setBooking(true);
    try {
      const { data: appt } = await api.post("/appointments", {
        professional_id: id,
        scheduled_at: slot,
        reason,
      });
      // SUS patients skip payment — appointment is already confirmed
      if (appt.is_sus) {
        toast.success("Consulta agendada pelo SUS!");
        nav("/dashboard");
        return;
      }
      const { data: co } = await api.post("/payments/checkout", {
        appointment_id: appt.id,
        origin_url: window.location.origin,
      });
      window.location.href = co.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBooking(false);
    }
  };

  if (!pro) return <div className="container-optia py-12 text-slate-500">Carregando…</div>;

  return (
    <div className="container-optia py-12 animate-fade-in">
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="flex gap-5 items-start">
            {pro.avatar ? (
              <img src={pro.avatar} alt={pro.name} className="h-28 w-28 rounded-3xl object-cover" />
            ) : (
              <div className="h-28 w-28 rounded-3xl bg-teal-100 grid place-items-center text-teal-700 font-bold text-4xl">{pro.name?.charAt(0)}</div>
            )}
            <div>
              <h1 className="font-display text-3xl font-semibold text-navy-900" data-testid="prof-name">{pro.name}</h1>
              <p className="text-teal-700 mt-1">{pro.specialty}</p>
              <div className="flex items-center gap-3 mt-2 text-sm text-slate-600">
                <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {pro.rating}</span>
                <span>·</span>
                <span>{pro.reviews_count || 0} avaliações</span>
              </div>
            </div>
          </div>

          <Card className="card-soft mt-8">
            <CardContent className="p-7">
              <h2 className="font-display text-xl font-semibold text-navy-900">Sobre</h2>
              <p className="text-slate-700 mt-3 leading-relaxed">{pro.bio}</p>
              <div className="grid sm:grid-cols-3 gap-3 mt-6">
                <div className="flex items-start gap-2"><Award className="w-4 h-4 text-teal-600 mt-1" /><div><div className="text-xs text-slate-500">Cuidado afirmativo</div><div className="text-sm text-navy-900 font-medium">Neurodiversidade</div></div></div>
                <div className="flex items-start gap-2"><Video className="w-4 h-4 text-teal-600 mt-1" /><div><div className="text-xs text-slate-500">Modalidade</div><div className="text-sm text-navy-900 font-medium">100% Online</div></div></div>
                <div className="flex items-start gap-2"><Clock className="w-4 h-4 text-teal-600 mt-1" /><div><div className="text-xs text-slate-500">Duração</div><div className="text-sm text-navy-900 font-medium">50 minutos</div></div></div>
              </div>
              <div className="mt-6">
                <div className="text-sm text-slate-500 mb-2">Áreas de expertise</div>
                <div className="flex flex-wrap gap-2">
                  {(pro.expertise || []).map((e) => <Badge key={e} className="bg-teal-50 text-teal-800 border-0">{e}</Badge>)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <aside className="lg:col-span-1">
          <Card className="card-soft sticky top-24">
            <CardContent className="p-6">
              <div className="text-slate-500 text-sm">Consulta particular</div>
              <div className="font-display text-3xl font-semibold text-navy-900 mt-1">{user?.municipality_id ? "Grátis pelo SUS" : `R$ ${pro.price_per_session}`}</div>
              <p className="text-xs text-slate-500">{user?.municipality_id ? "Sem pagamento — convênio SUS" : "Pagamento seguro via Stripe"}</p>
              {!user?.municipality_id && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid="stripe-sandbox-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Modo Teste — Sandbox
                </div>
              )}
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full mt-5 bg-teal-500 hover:bg-teal-600 h-11 rounded-xl" data-testid="book-btn">Agendar consulta</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle className="font-display text-navy-900">Agendar com {pro.name}</DialogTitle></DialogHeader>
                  <div className="grid sm:grid-cols-2 gap-4 mt-3">
                    <div>
                      <div className="text-sm font-medium mb-2">Data</div>
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d)=>d < new Date(new Date().setHours(0,0,0,0))} className="rounded-xl border" data-testid="book-calendar" />
                    </div>
                    <div>
                      <div className="text-sm font-medium mb-2">Horário</div>
                      {loadingSlots ? (
                        <div className="text-sm text-slate-500 py-3">Carregando horários…</div>
                      ) : slots.length === 0 ? (
                        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">Sem horários livres neste dia. Escolha outra data.</div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto pr-1" data-testid="book-slots">
                          {slots.map((iso) => (
                            <button key={iso} onClick={()=>setSlot(iso)} className={`py-2 rounded-lg text-sm border transition ${slot===iso ? "bg-teal-500 border-teal-500 text-white" : "border-slate-300 hover:border-teal-400"}`} data-testid={`slot-${iso}`}>{fmtTime(iso)}</button>
                          ))}
                        </div>
                      )}
                      <div className="mt-4">
                        <div className="text-sm font-medium mb-2">Motivo (opcional)</div>
                        <Textarea value={reason} onChange={(e)=>setReason(e.target.value)} rows={3} placeholder="Se sentir à vontade, conte um pouco…" data-testid="book-reason" />
                      </div>
                    </div>
                  </div>
                  <DialogFooter className="mt-5">
                    <Button onClick={book} disabled={booking} className="bg-teal-500 hover:bg-teal-600" data-testid="book-confirm">
                      {booking ? "Processando..." : (user?.municipality_id ? "Confirmar agendamento (SUS)" : `Ir para pagamento — R$ ${pro.price_per_session}`)}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              {user && user.role === "patient" && (
                <Button variant="outline" className="w-full mt-3 rounded-xl" onClick={()=>nav(`/mensagens/${pro.id}`)} data-testid="message-btn">Enviar mensagem</Button>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}
